import { imageFieldSchema } from '@/domain/image/imageFieldSchema';
import { ImageField } from '@/domain/image/ImageField';
import { InvalidProjectFormatError } from '@/domain/template/InvalidProjectFormatError';
import { PROJECT_FORMAT_VERSION, readProjectFormatVersion } from '@/domain/template/projectFormatVersion';
import { TextboxField } from '@/domain/textbox/TextboxField';
import { textboxFieldSchema } from '@/domain/textbox/textboxFieldSchema';
import type { AField } from '@/domain/field/AField';
import type { TImageFieldJSONData } from '@/domain/image/imageFieldSchema';
import type { ITemplateConfiguration } from '@/domain/template/ITemplateConfiguration';
import type { TTextboxFieldJSONData } from '@/domain/textbox/textboxFieldSchema';

// The shape of a saved project, owned by the domain: no env, no transport, no URL. Building the
// DTO around it and turning a file id into a URL are api/ concerns — see api/templateMapper.ts.

// Both save paths go through this, so `version` lands on every row: a version stamped on only
// some of them tells a migration nothing. Fields are mapped explicitly rather than spread —
// leaving JSON.stringify to call toJSON() later works, but it is invisible at the call site.
function configurationToProjectJson(configuration: ITemplateConfiguration): Record<string, unknown> {
    return {
        version: PROJECT_FORMAT_VERSION,
        backgroundColor: configuration.backgroundColor,
        backgroundColorVerso: configuration.backgroundColorVerso,
        bat: configuration.bat,
        batPreview: configuration.batPreview,
        fields: configuration.fields.map((field) => field.toJSON()),
    };
}

function projectJsonToConfiguration(json: Record<string, unknown>): ITemplateConfiguration {
    // A payload that is not an object cannot be read field by field. Refusing it here is what
    // makes it a format error the loading paths already report: reaching into it instead threw
    // a TypeError, which no caller recognises, so a truncated draft reached the caller as a
    // programming error and left the editor unmounted.
    if (json === null || typeof json !== 'object' || Array.isArray(json)) {
        throw new InvalidProjectFormatError(`project payload is ${Array.isArray(json) ? 'an array' : typeof json}, expected an object`);
    }

    const rawFields = Array.isArray(json.fields) ? (json.fields as Record<string, unknown>[]) : [];
    // The version is read here rather than branched on: there is one format, so there is nothing
    // to migrate yet and a branch would have no second arm. What it does earn its place for is
    // the refusal message -- "written before versioning" and "written as v1 and corrupt" are
    // different problems, and only this tells them apart.
    const version = readProjectFormatVersion(json);
    const fields = rawFields.map((raw) => _jsonToField(raw, version));

    return {
        backgroundColor: typeof json.backgroundColor === 'string' ? json.backgroundColor : undefined,
        backgroundColorVerso: typeof json.backgroundColorVerso === 'string' ? json.backgroundColorVerso : undefined,
        bat: typeof json.bat === 'string' ? json.bat : undefined,
        batPreview: typeof json.batPreview === 'string' ? json.batPreview : undefined,
        fields,
    };
}

// Validated before construction, and it throws rather than repairing. fromJSON casts its
// argument, so an unchecked field builds a half-formed entity that fails later, somewhere else.
// The schemas were written and tested but never run in production — this is the call that was
// missing, not the guard.
function _jsonToField(json: Record<string, unknown>, formatVersion: number): AField {
    if (json.kind === 'image') {
        return ImageField.fromJSON(_validated(imageFieldSchema, json, formatVersion) as unknown as TImageFieldJSONData);
    }
    if (json.kind === 'textbox') {
        return TextboxField.fromJSON(_validated(textboxFieldSchema, json, formatVersion) as unknown as TTextboxFieldJSONData);
    }

    throw new InvalidProjectFormatError(`v${formatVersion} project: field of unknown kind ${JSON.stringify(json.kind)}`);
}

function _validated(
    schema: { validateSync: (value: unknown, options: { abortEarly: boolean }) => unknown },
    json: Record<string, unknown>,
    formatVersion: number,
) {
    try {
        return schema.validateSync(json, { abortEarly: false });
    } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);

        throw new InvalidProjectFormatError(
            `v${formatVersion} project: field ${JSON.stringify(json.id)} of kind ${JSON.stringify(json.kind)}: ${reason}`,
        );
    }
}

export { configurationToProjectJson, projectJsonToConfiguration };
